import { FunnelChart, Funnel, LabelList, Tooltip, ResponsiveContainer } from 'recharts';
import { formatStageLabel, STAGE_ORDER } from '../../utils/formatters';

// Navy-to-gold gradient matching the portal palette. Extended to cover all 13
// canonical stages (Item #2); the six post-DD colours reuse/rotate the palette
// so no stage index resolves to an undefined fill.
const STAGE_COLOURS = [
  '#1a2744', // pif_submitted — deepest navy
  '#1e3a6e', // analyst_review
  '#2a5298', // peer_review
  '#3a6fc4', // hop_review
  '#c9952a', // iic_approval — amber transition
  '#e6b84a', // screening — gold
  '#1e3a6e', // iic_approval_ic1
  '#2a5298', // ic1
  '#3a6fc4', // due_diligence
  '#c9952a', // iic_approval_ic2
  '#e6b84a', // ic2
  '#2d7a4f', // post_ic2 — green (deployment)
  '#27ae60', // invested — green (portfolio management)
];

function computeFunnel(deals) {
  // Base for cumulative rate: ALL deals ever submitted to this fund
  // regardless of current status or stage.
  const totalDeals = deals.length;

  const rows = STAGE_ORDER.map((stage, stageIdx) => {
    const inProgress = deals.filter(
      d => d.current_stage === stage && d.status === 'active'
    ).length;

    const advanced = deals.filter(d => {
      const dIdx = STAGE_ORDER.indexOf(d.current_stage);
      return dIdx > stageIdx ||
        (!STAGE_ORDER.includes(d.current_stage) && d.status === 'active');
    }).length;

    const dropped = deals.filter(
      d => d.current_stage === stage && (d.status === 'denied' || d.status === 'suspended')
    ).length;

    const entered = inProgress + advanced + dropped;
    const rate = entered > 0 ? Math.round((advanced / entered) * 100) : null;

    // Cumulative rate: % of ALL deals ever submitted that reached this stage
    const cumulativeRate = totalDeals > 0
      ? Math.round((entered / totalDeals) * 100)
      : null;

    return { stage, label: formatStageLabel(stage), entered, advanced, inProgress, dropped, rate, cumulativeRate };
  }).filter(s => s.entered > 0);

  return rows;
}

// Custom tooltip showing both rates
function CustomTooltip({ active, payload }) {
  if (!active || !payload?.[0]) return null;
  const d = payload[0].payload;
  return (
    <div style={{
      background: 'var(--color-card)', border: '1px solid var(--color-border)',
      borderRadius: 6, padding: '10px 14px', fontSize: 13, boxShadow: 'var(--shadow-md)',
    }}>
      <p style={{ fontWeight: 700, marginBottom: 6 }}>{d.label}</p>
      <p>Deals entered: <strong>{d.entered}</strong></p>
      <p>In progress: <strong>{d.inProgress}</strong></p>
      <p>Advanced: <strong>{d.advanced}</strong></p>
      <p>Dropped: <strong>{d.dropped}</strong></p>
      {d.rate !== null && (
        <p style={{ marginTop: 6, borderTop: '1px solid var(--color-border)', paddingTop: 6 }}>
          Stage conversion: <strong>{d.rate}%</strong>
        </p>
      )}
      {d.cumulativeRate !== null && (
        <p>Cumulative from PIF: <strong>{d.cumulativeRate}%</strong></p>
      )}
    </div>
  );
}

export default function FunnelConversionView({ deals }) {
  const data = computeFunnel(deals);

  if (!data.length) {
    return <p className="text-muted" style={{ fontSize: 13 }}>No stage data available.</p>;
  }

  // recharts FunnelChart data format
  const funnelData = data.map((row, i) => ({
    name: row.label,
    value: row.entered,
    fill: STAGE_COLOURS[STAGE_ORDER.indexOf(row.stage)] || STAGE_COLOURS[0],
    // Carry through all computed fields for the custom tooltip
    ...row,
  }));

  return (
    <div>
      {/* Funnel chart */}
      <div className="card mb-4">
        <div className="card-header"><h2>Pipeline Funnel</h2></div>
        <div className="card-body">
          <ResponsiveContainer width="100%" height={320}>
            <FunnelChart>
              <Tooltip content={<CustomTooltip />} />
              <Funnel
                dataKey="value"
                data={funnelData}
                isAnimationActive
              >
                <LabelList
                  position="center"
                  content={({ x, y, width, height, value, index }) => {
                    const row = data[index];
                    if (!row) return null;
                    return (
                      <text
                        x={x + width / 2}
                        y={y + height / 2}
                        textAnchor="middle"
                        dominantBaseline="middle"
                        style={{ fill: '#fff', fontSize: 12, fontWeight: 600 }}
                      >
                        {row.label}: {value}
                      </text>
                    );
                  }}
                />
              </Funnel>
            </FunnelChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Summary table with both rate columns */}
      <div className="card">
        <div className="card-header"><h2>Conversion Summary</h2></div>
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Stage</th>
                <th>Entered</th>
                <th>Advanced</th>
                <th>In Progress</th>
                <th>Dropped</th>
                <th>Stage Rate</th>
                <th>Cumulative Rate</th>
              </tr>
            </thead>
            <tbody>
              {data.map(row => (
                <tr key={row.stage}>
                  <td style={{ fontWeight: 500 }}>
                    <span style={{
                      display: 'inline-block', width: 10, height: 10, borderRadius: 2,
                      background: STAGE_COLOURS[STAGE_ORDER.indexOf(row.stage)] || '#999',
                      marginRight: 8,
                    }} />
                    {row.label}
                  </td>
                  <td>{row.entered}</td>
                  <td style={{ color: 'var(--color-success)' }}>{row.advanced}</td>
                  <td style={{ color: 'var(--color-accent)' }}>{row.inProgress}</td>
                  <td style={{ color: 'var(--color-error)' }}>{row.dropped}</td>
                  <td>
                    {row.rate !== null
                      ? <strong>{row.rate}%</strong>
                      : <span className="text-muted">—</span>}
                  </td>
                  <td>
                    {row.cumulativeRate !== null
                      ? <strong>{row.cumulativeRate}%</strong>
                      : <span className="text-muted">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p style={{ fontSize: 12, color: 'var(--color-text-secondary)', padding: '8px 16px 12px', margin: 0 }}>
          <strong>Stage Rate:</strong> % of deals reaching this stage that advanced to the next stage.{' '}
          <strong>Cumulative Rate:</strong> % of all deals ever submitted to this fund that reached this stage.
        </p>
      </div>
    </div>
  );
}
